/**
 * PaymentProofDetailModal Component
 * Modal showing detailed information about a payment proof
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import type { PaymentProof } from "../../../store/api/paymentProofsApi";

interface PaymentProofDetailModalProps {
	proof: PaymentProof;
	isOpen: boolean;
	onClose: () => void;
	onApprove: () => void;
	onReject: () => void;
}

const PaymentProofDetailModal: React.FC<PaymentProofDetailModalProps> = ({
	proof,
	isOpen,
	onClose,
	onApprove,
	onReject,
}) => {
	const navigate = useNavigate();

	if (!isOpen) return null;

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const getStatusBadge = (status: string) => {
		const statusConfig: Record<
			string,
			{ color: string; label: string }
		> = {
			pending: { color: "bg-warning/10 text-warning", label: "Pending" },
			approved: { color: "bg-success/10 text-success", label: "Approved" },
			rejected: { color: "bg-error/10 text-error", label: "Rejected" },
		};

		const config = statusConfig[status] || statusConfig.pending;
		return (
			<span
				className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}
			>
				{config.label}
			</span>
		);
	};

	const getRequestTypeBadge = (requestType?: string) => {
		if (!requestType) return null;

		const typeConfig: Record<
			string,
			{ color: string; label: string }
		> = {
			lease: { color: "bg-blue-100 text-blue-800 dark:bg-blue-950/20 dark:text-blue-400", label: "Lease Request" },
			buy: { color: "bg-purple-100 text-purple-800 dark:bg-purple-950/20 dark:text-purple-400", label: "Buy Request" },
		};

		const config = typeConfig[requestType.toLowerCase()] || { color: "bg-muted text-muted-foreground", label: requestType };
		return (
			<span
				className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
			>
				{config.label}
			</span>
		);
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
			<div className="bg-card border border-border rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
				{/* Header */}
				<div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
					<div>
						<h2 className="text-xl font-bold text-foreground">Payment Proof Details</h2>
						<p className="text-sm text-muted-foreground mt-1">
							Proof ID: {proof.id.slice(-8)}
						</p>
					</div>
					<button
						onClick={onClose}
						className="text-muted-foreground hover:text-foreground smooth-transition"
					>
						<Icon name="X" size={24} />
					</button>
				</div>

				{/* Content */}
				<div className="p-6 space-y-6">
					{/* Status and Request Type */}
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-3">
							<div>
								<span className="text-sm font-medium text-muted-foreground">Status: </span>
								{getStatusBadge(proof.status)}
							</div>
							{proof.request_type && (
								<div>
									{getRequestTypeBadge(proof.request_type)}
								</div>
							)}
						</div>
					</div>

					{/* Property Information */}
					<div className="bg-muted/30 rounded-lg p-4">
						<h3 className="font-semibold text-foreground mb-3">Property Information</h3>
						<div className="space-y-2 text-sm">
							<div className="flex items-center justify-between">
								<p>
									<span className="text-muted-foreground">Property:</span>{" "}
									<span className="text-foreground font-medium">
										{proof.property_name || "N/A"}
									</span>
								</p>
								{proof.property_id && (
									<Button
										variant="ghost"
										size="sm"
										onClick={() => {
											onClose();
											navigate(`/property-management?property_id=${proof.property_id}`);
										}}
										className="h-7 text-xs"
									>
										<Icon name="ExternalLink" size={14} className="mr-1" />
										View Property
									</Button>
								)}
							</div>
							{proof.property_address && (
								<p>
									<span className="text-muted-foreground">Address:</span>{" "}
									<span className="text-foreground">
										{proof.property_address}
									</span>
								</p>
							)}
							{proof.property_request_id && (
								<div className="flex items-center justify-between pt-2 border-t border-border/50">
									<p>
										<span className="text-muted-foreground">Request ID:</span>{" "}
										<span className="text-foreground font-mono text-xs">
											{proof.property_request_id.slice(-8)}
										</span>
									</p>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => {
											onClose();
											navigate(`/property-requests-management?request_id=${proof.property_request_id}`);
										}}
										className="h-7 text-xs"
									>
										<Icon name="ExternalLink" size={14} className="mr-1" />
										View Request
									</Button>
								</div>
							)}
						</div>
					</div>

					{/* User/Tenant Information */}
					{proof.user_name && (
						<div className="bg-muted/30 rounded-lg p-4">
							<h3 className="font-semibold text-foreground mb-3">Tenant Information</h3>
							<div className="space-y-2 text-sm">
								<div className="flex items-center justify-between">
									<p>
										<span className="text-muted-foreground">Name:</span>{" "}
										<span className="text-foreground font-medium">
											{proof.user_name}
										</span>
									</p>
									{proof.user_id && (
										<Button
											variant="ghost"
											size="sm"
											onClick={() => {
												onClose();
												navigate(`/user-management?user_id=${proof.user_id}`);
											}}
											className="h-7 text-xs"
										>
											<Icon name="ExternalLink" size={14} className="mr-1" />
											View User
										</Button>
									)}
								</div>
							</div>
						</div>
					)}

					{/* Payment Information */}
					<div className="bg-muted/30 rounded-lg p-4">
						<h3 className="font-semibold text-foreground mb-3">Payment Information</h3>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
							<div>
								<p className="text-muted-foreground">Amount</p>
								<p className="text-foreground font-medium">
									{formatCurrency(proof.amount)}
								</p>
							</div>
							<div>
								<p className="text-muted-foreground">Payment Date</p>
								<p className="text-foreground font-medium">
									{formatDate(proof.payment_date)}
								</p>
							</div>
							<div>
								<p className="text-muted-foreground">Payment Method</p>
								<p className="text-foreground font-medium">
									{proof.payment_method}
								</p>
							</div>
							{proof.transaction_reference && (
								<div>
									<p className="text-muted-foreground">Transaction Reference</p>
									<p className="text-foreground font-medium font-mono">
										{proof.transaction_reference}
									</p>
								</div>
							)}
						</div>
						{proof.notes && (
							<div className="mt-4">
								<p className="text-muted-foreground mb-1">Notes</p>
								<p className="text-foreground">{proof.notes}</p>
							</div>
						)}
					</div>

					{/* Proof Document */}
					<div className="bg-muted/30 rounded-lg p-4">
						<h3 className="font-semibold text-foreground mb-3">Proof Document</h3>
						<div className="flex items-center space-x-4">
							<a
								href={proof.proof_document_url}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center space-x-2 text-primary hover:underline"
							>
								<Icon name="FileText" size={20} />
								<span>View Document</span>
							</a>
						</div>
					</div>

					{/* Review Information */}
					{proof.reviewed_at && (
						<div className="bg-muted/30 rounded-lg p-4">
							<h3 className="font-semibold text-foreground mb-3">Review Information</h3>
							<div className="space-y-2 text-sm">
								<p>
									<span className="text-muted-foreground">Reviewed:</span>{" "}
									<span className="text-foreground">
										{formatDate(proof.reviewed_at)}
									</span>
								</p>
								{proof.review_notes && (
									<div>
										<p className="text-muted-foreground mb-1">Review Notes</p>
										<p className="text-foreground">{proof.review_notes}</p>
									</div>
								)}
							</div>
						</div>
					)}

					{/* Actions */}
					{proof.status === "pending" && (
						<div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
							<Button variant="outline" onClick={onClose}>
								Cancel
							</Button>
							<Button variant="outline" onClick={onReject}>
								<Icon name="X" size={16} className="mr-2" />
								Reject
							</Button>
							<Button onClick={onApprove}>
								<Icon name="Check" size={16} className="mr-2" />
								Approve
							</Button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default PaymentProofDetailModal;
